/**
 * Cognito, owned by the web client.
 *
 * This is the one piece of "behaviour" the browser keeps rather than pushing to the
 * server, because it is not app logic — it is a vendor SDK doing a standard auth dance,
 * and proxying it through our own API would only add a hop that can fail.
 *
 * Tokens are held in localStorage. That is forced, not chosen: amazon-cognito-identity-js
 * requires a SYNCHRONOUS storage, and given an async one it silently falls back to memory
 * and signs the user out on every reload. The mitigation is the CSP and the dedicated
 * origin (see infra/lib/saddle-web-stack.ts), not a different store.
 *
 * The ACCESS token is what every Saddle origin wants — the API verifies token_use ===
 * 'access' and rejects an ID token with a 401 even though the same pool signed it.
 */
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  type ICognitoStorage,
} from 'amazon-cognito-identity-js';

export interface SessionUser {
  id: string;
  email: string;
}

export interface CognitoSettings {
  userPoolId: string;
  clientId: string;
}

/**
 * The SAME storage the shared CognitoService writes through.
 *
 * Not a second localStorage wrapper. `@/storage/mmkv` is aliased to platform/mmkv.web.ts
 * for the browser, and that shim TYPE-TAGS what it stores — a token is written as
 * `s:<jwt>`. A wrapper reading window.localStorage directly would get the tag back as part
 * of the value: a corrupted JWT, a 401 on every call, and a sign-in loop. Two wrappers
 * over one session is how the last sign-out bug happened; this is one implementation with
 * two consumers, until the screens stop using CognitoService entirely.
 */
import { mmkv, storage as kv } from '../platform/mmkv.web';

const COGNITO_PREFIX = 'CognitoIdentityServiceProvider.';

const storage: ICognitoStorage = {
  getItem: (key) => mmkv.getString(key) ?? null,
  setItem: (key, value) => mmkv.setString(key, value),
  removeItem: (key) => {
    mmkv.delete(key);
  },
  /** Only Cognito's own keys — never the rest of the app's data. */
  clear: () => {
    kv.getAllKeys()
      .filter((key) => key.startsWith(COGNITO_PREFIX))
      .forEach((key) => kv.remove(key));
  },
};

let settings: CognitoSettings = { userPoolId: '', clientId: '' };
let pool: CognitoUserPool | null = null;

export function configureAuth(next: CognitoSettings): void {
  settings = next;
  pool = null;
}

/** Null when auth is not configured, rather than throwing on an empty pool id. */
function userPool(): CognitoUserPool | null {
  if (pool) return pool;
  if (!settings.userPoolId || !settings.clientId) return null;
  pool = new CognitoUserPool({
    UserPoolId: settings.userPoolId,
    ClientId: settings.clientId,
    Storage: storage,
  });
  return pool;
}

export function authConfigured(): boolean {
  return Boolean(settings.userPoolId && settings.clientId);
}

/**
 * An email as the pool should see it: trimmed and lowercased, as the phone's
 * `normalizeIdentifier` does.
 *
 * The pool is case-SENSITIVE (UsernameConfiguration is null), and the phone and the published
 * sites' sign-up page share it with this client. Only trimmed here, "Me@x.com" typed in the
 * browser and "me@x.com" typed on the phone could be two different Saddle accounts — or one
 * account that each client could not find.
 */
export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * The pool's UsernameAttributes is email/phone, so the EMAIL is the username. Passing a
 * generated one throws InvalidParameterException — a regression this product has shipped
 * before. Taken exactly as given: which spelling to ask for is each caller's decision.
 */
function cognitoUser(username: string): CognitoUser {
  const active = userPool();
  if (!active) throw new Error('Auth is not configured.');
  // Same synchronous storage as the pool: with the SDK's async default, sign-in would
  // cache tokens where the pool's getCurrentUser() cannot read them on a cold start.
  return new CognitoUser({ Username: username, Pool: active, Storage: storage });
}

/** Cognito's error name survives the rethrow, so a caller can tell the failures apart. */
type AuthFailure = Error & { code?: string };

function failure(err: AuthFailure): AuthFailure {
  const out: AuthFailure = new Error(err.message);
  out.code = err.code ?? err.name;
  return out;
}

const codeOf = (err: unknown): string | undefined => (err as AuthFailure | null)?.code;

/**
 * Ask for the account lowercased and — only when the person typed capitals and the pool answered
 * as though there were no such account — once more exactly as typed.
 *
 * Accounts this browser created before sign-up lowercased are stored as they were typed, and the
 * pool is case-sensitive: "Me@x.com" is only ever found as "Me@x.com". Lowercasing alone would
 * have locked every one of them out. The pool hides whether an account exists
 * (PreventUserExistenceErrors), so "no such account" and "wrong password" (or "wrong code") are
 * one answer; a wrong one fails both spellings the same way, and the FIRST refusal is reported.
 * A different answer to the second (not confirmed, say) is about the account that exists.
 */
async function eitherSpelling<T>(
  email: string,
  act: (username: string) => Promise<T>,
  asIfMissing: (err: unknown) => boolean,
): Promise<T> {
  const username = normalizeEmail(email);
  try {
    return await act(username);
  } catch (first) {
    const typed = email.trim();
    if (typed === username || !asIfMissing(first)) throw first;
    try {
      return await act(typed);
    } catch (second) {
      throw asIfMissing(second) ? first : second;
    }
  }
}

function userFrom(session: CognitoUserSession): SessionUser {
  const payload = session.getIdToken().decodePayload() as { sub?: string; email?: string };
  return { id: payload.sub ?? '', email: payload.email ?? '' };
}

export async function signUp(
  email: string,
  password: string,
  opts: { acceptedTermsAt?: string } = {},
): Promise<void> {
  const active = userPool();
  if (!active) throw new Error('Auth is not configured.');
  const username = normalizeEmail(email);
  const attributes = [
    new CognitoUserAttribute({ Name: 'email', Value: username }),
    new CognitoUserAttribute({
      Name: 'custom:terms_accepted_at',
      Value: opts.acceptedTermsAt ?? new Date().toISOString(),
    }),
  ];
  return new Promise((resolve, reject) => {
    active.signUp(username, password, attributes, [], (err) =>
      err ? reject(new Error(err.message)) : resolve(),
    );
  });
}

/**
 * Confirm and resend follow a sign-up, which now always stores the account lowercased; the
 * Confirm screen carries the email as it was typed, so they lowercase it too.
 */
export async function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(normalizeEmail(email)).confirmRegistration(code.trim(), true, (err) =>
      err ? reject(new Error(err.message)) : resolve(),
    );
  });
}

export async function resendConfirmation(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(normalizeEmail(email)).resendConfirmationCode((err) => (err ? reject(new Error(err.message)) : resolve()));
  });
}

function authenticate(username: string, password: string): Promise<SessionUser> {
  const user = cognitoUser(username);
  return new Promise((resolve, reject) => {
    user.authenticateUser(new AuthenticationDetails({ Username: username, Password: password }), {
      onSuccess: (session) => resolve(userFrom(session)),
      onFailure: (err: AuthFailure) => reject(failure(err)),
      newPasswordRequired: () => reject(new Error('This account needs a new password. Please contact support.')),
    });
  });
}

export async function signIn(email: string, password: string): Promise<SessionUser> {
  // A wrong email and a wrong password are one answer: the pool hides which.
  return eitherSpelling(email, (username) => authenticate(username, password), (err) =>
    codeOf(err) === 'NotAuthorizedException' || codeOf(err) === 'UserNotFoundException',
  );
}

export async function signOut(): Promise<void> {
  userPool()?.getCurrentUser()?.signOut();
  storage.clear();
}

function requestReset(username: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(username).forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err: Error) => reject(new Error(err.message)),
      inputVerificationCode: () => resolve(),
    });
  });
}

/**
 * Send a reset code to the account, lowercased — and, when the person typed capitals, to the
 * spelling they typed as well.
 *
 * Unlike sign-in there is no refusal to fall back on: the pool answers a reset for an account
 * that does not exist exactly as it answers one that does (PreventUserExistenceErrors), so an
 * account made here before sign-up lowercased would never get its code and nobody would be told.
 * Asking for both spellings costs nothing: only the account that exists is sent a code.
 */
export async function forgotPassword(email: string): Promise<void> {
  const username = normalizeEmail(email);
  await requestReset(username);
  const typed = email.trim();
  if (typed !== username) await requestReset(typed);
}

export async function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  // A code checked against an account that does not exist reads as a wrong or spent code.
  return eitherSpelling(
    email,
    (username) =>
      new Promise<void>((resolve, reject) => {
        cognitoUser(username).confirmPassword(code.trim(), newPassword, {
          onSuccess: () => resolve(),
          onFailure: (err: AuthFailure) => reject(failure(err)),
        });
      }),
    (err) => ['CodeMismatchException', 'ExpiredCodeException', 'UserNotFoundException'].includes(codeOf(err) ?? ''),
  );
}

/** Resolves the stored session, refreshing it if the SDK decides it must. */
export async function currentUser(): Promise<SessionUser | null> {
  const user = userPool()?.getCurrentUser();
  if (!user) return null;
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        storage.clear();
        resolve(null);
        return;
      }
      resolve(userFrom(session));
    });
  });
}

/**
 * The access token, read synchronously.
 *
 * Synchronous because every API call needs it and the SDK's session callback fires
 * immediately when the session is already cached. Returns null rather than a stale token
 * once it has expired, so a caller gets a clean 401 instead of a confusing one.
 */
export function accessToken(): string | null {
  const user = userPool()?.getCurrentUser();
  if (!user) return null;
  let token: string | null = null;
  user.getSession((err: Error | null, session: CognitoUserSession | null) => {
    if (!err && session?.isValid()) token = session.getAccessToken().getJwtToken();
  });
  return token;
}

export async function deleteAccount(): Promise<void> {
  const user = userPool()?.getCurrentUser();
  if (!user) throw new Error('You are not signed in.');
  await new Promise<void>((resolve, reject) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        reject(new Error('Your session has expired. Please sign in again.'));
        return;
      }
      user.deleteUser((delErr) => {
        if (delErr) {
          reject(new Error(delErr.message));
          return;
        }
        storage.clear();
        resolve();
      });
    });
  });
}
